import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { BRAND_NAME, getBrandFallbackMarkSrc, getBrandLogoSrc } from '@/lib/brandConstants';

/**
 * Premium brand header for funnel forms (/start, /payment, /school).
 * Matches the main nav: `public/assets/logo.png` + ChooseRightCareer wordmark.
 *
 * @param {{ variant?: 'default'|'school', wordmarkTitle?: string, wordmarkSubtitle?: string }} [props]
 */
export default function BrandFormChrome({ variant = 'default', wordmarkTitle, wordmarkSubtitle }) {
  const [markSrc, setMarkSrc] = useState(getBrandLogoSrc);
  const title = String(wordmarkTitle || '').trim() || BRAND_NAME;
  const sub =
    String(wordmarkSubtitle || '').trim() ||
    (variant === 'school'
      ? 'School program · same science-backed Career DNA read'
      : 'Science-backed career clarity · Career DNA');

  return (
    <div className="relative z-10 mx-auto mb-8 flex max-w-lg flex-col items-center px-1 text-center">
      <Link
        to="/"
        className="group flex max-w-md flex-col items-center gap-3 rounded-2xl px-4 py-2 outline-none transition hover:opacity-[0.98] focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 sm:flex-row sm:items-center sm:gap-4 sm:text-left"
      >
        <div className="relative shrink-0">
          <div
            className="absolute -inset-1 rounded-full bg-gradient-to-br from-blue-500/25 via-sky-400/20 to-teal-500/25 blur-md transition group-hover:from-blue-500/35 group-hover:to-teal-500/35"
            aria-hidden
          />
          <img
            src={markSrc}
            alt={`${title} · ${BRAND_NAME}`}
            onError={() => setMarkSrc((s) => (s === getBrandFallbackMarkSrc() ? s : getBrandFallbackMarkSrc()))}
            width={112}
            height={112}
            decoding="async"
            className="relative h-14 w-14 rounded-full object-contain shadow-lg shadow-blue-900/15 ring-2 ring-white/90 transition group-hover:shadow-xl group-hover:ring-sky-100 sm:h-16 sm:w-16"
          />
        </div>
        <div className="min-w-0 space-y-1">
          <span className="font-display block bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-xl font-bold tracking-tight text-transparent sm:text-2xl md:text-[1.65rem]">
            {title}
          </span>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">{sub}</p>
        </div>
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-medium text-slate-600">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200/80 bg-white/70 px-2.5 py-1 shadow-sm">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-teal-600" aria-hidden />
          Secure checkout &amp; data
        </span>
        <span className="rounded-full border border-blue-100/90 bg-blue-50/60 px-2.5 py-1 text-slate-600">
          34-question Career DNA
        </span>
      </div>
    </div>
  );
}
